import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { EventClientEntity } from './event-client.entity';
import { EventLinkEntity } from './event-link.entity';
import { EventOccurrenceEntity } from './event-occurrence.entity';
import { EventSubtaskEntity } from './event-subtask.entity';
import { EventUserEntity } from './event-user.entity';

@Index('_id_event__index', ['id'], { unique: true })
@Index('idx_event_user_deleted', ['userId', 'deletedDate'])
// PD-16477 — an import looks its earlier tasks up by external id on every run.
@Index('idx_event_external_id', ['externalId'])
@Entity('_event')
export class EventEntity extends BaseEntity {
	@Column('varchar', { name: '_type', nullable: true, length: 64 })
	public type!: string | null;

	@Column('text', { name: '_subject', nullable: true })
	public subject!: string | null;

	@Column('text', { name: '_description', nullable: true })
	public description!: string | null;

	@Column('date', { name: '_startDate', nullable: true })
	public startDate!: string | null;

	@Column('time', { name: '_timeOfDay', nullable: true })
	public timeOfDay!: string | null;

	@Column('varchar', { name: '_timezone', nullable: true, length: 256 })
	public timezone!: string | null;

	@Column('int', { name: '_duration', nullable: true })
	public duration!: number | null;

	@Column('date', { name: '_untilDate', nullable: true })
	public untilDate!: string | null;

	@Column('text', { name: '_rrule', nullable: true })
	public rrule!: string | null;

	@Column('json', { name: '_exdates', nullable: true })
	public exdates!: Array<string> | null;

	@Column('int', { name: '_userID', nullable: true, unsigned: true })
	public userId!: number | null;

	@Column('int', {
		name: '_trigger_group_state_history__id',
		nullable: true,
		unsigned: true,
	})
	public triggerGroupStateHistoryId!: number | null;

	@Column('varchar', { name: '_subtype', nullable: true, length: 64 })
	public subtype!: string | null;

	@Column('varchar', {
		name: 'm_sm_priority',
		nullable: true,
		length: 64,
		default: () => "'2'",
	})
	public mSmPriority!: string | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	/**
	 * PD-16477 — the id of the record in the outside system a task or event was
	 * imported from. A re-runnable import uses it to find the row it already
	 * created instead of adding a second one. Null on everything created in
	 * Workd. Deliberately not unique: nothing here should fail because a row was
	 * copied.
	 *
	 * SM1 exposure: none. The legacy CRM does not name the column when it
	 * inserts or reads events, and the column is nullable with no default.
	 */
	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	@OneToMany(
		() => EventOccurrenceEntity,
		(eventOccurrence) => eventOccurrence.fkEvent,
		{},
	)
	public eventOccurrences!: Array<EventOccurrenceEntity>;

	@OneToMany(
		() => EventSubtaskEntity,
		(eventSubtask) => eventSubtask.fkEvent,
		{},
	)
	public eventSubtasks!: Array<EventSubtaskEntity>;

	@OneToMany(() => EventClientEntity, (eventClient) => eventClient.fkEvent, {})
	public eventClients!: Array<EventClientEntity>;

	@OneToMany(() => EventUserEntity, (eventUser) => eventUser.fkEvent, {})
	public eventUsers!: Array<EventUserEntity>;

	@OneToMany(() => EventLinkEntity, (eventLink) => eventLink.fkEvent, {})
	public eventLinks!: Array<EventLinkEntity>;
}
